import React from 'react';

interface RefundPolicyPageProps {
  onBack: () => void;
}

const RefundPolicyPage: React.FC<RefundPolicyPageProps> = ({ onBack }) => {
  const lastUpdated = "9 January 2025";

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-primary-600 hover:text-primary-700 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-midnight">Refund Policy</h1>
          <p className="text-gray-600 mt-2">Last updated: {lastUpdated}</p>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Refund Policy outlines the terms and conditions regarding refunds for credits purchased 
              through AI Marker Mate. By purchasing credits, you acknowledge that you have read, understood, 
              and agree to be bound by this policy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This policy complies with Australian Consumer Law (ACL) and your rights as a consumer under 
              the Competition and Consumer Act 2010 (Cth).
            </p>
          </section>

          {/* No Refunds Policy */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">No Refunds on Credits</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-red-500 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Important Notice</h3>
                  <p className="text-red-700 font-medium">
                    Once credits are purchased, they are non-refundable under normal circumstances.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              All credit purchases are final. Credits purchased through our platform cannot be refunded, 
              returned, or exchanged for cash or other forms of compensation. This policy is clearly 
              stated at the point of purchase and forms part of our terms of service.
            </p>

            <p className="text-gray-700 leading-relaxed">
              By completing a credit purchase, you acknowledge and accept that:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Credits are digital goods consumed upon use</li>
              <li>Credits do not expire but are tied to your account</li>
              <li>Credits cannot be transferred between accounts</li>
              <li>Refunds are only available under specific circumstances outlined in this policy</li>
            </ul>
          </section>

          {/* Australian Consumer Law */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Australian Consumer Law Compliance</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              While our general policy is no refunds on credits, we fully comply with Australian Consumer Law, 
              which provides you with certain guaranteed consumer rights that cannot be excluded.
            </p>

            <h3 className="text-lg font-medium text-midnight mb-3">Your Consumer Guarantees</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under Australian Consumer Law, you are entitled to consumer guarantees that our services will:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Be of acceptable quality</li>
              <li>Be fit for the purpose they are intended</li>
              <li>Match any descriptions we provide</li>
              <li>Be delivered within a reasonable time</li>
              <li>Be provided with due care and skill</li>
            </ul>

            <h3 className="text-lg font-medium text-midnight mb-3">When Refunds May Be Available</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may be entitled to a remedy, including a refund, if:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Our service has a major failure (cannot be used for its intended purpose)</li>
              <li>There is a significant problem with service quality that cannot be remedied</li>
              <li>We fail to deliver the service within a reasonable time</li>
              <li>The service does not match our description or promotional material</li>
              <li>You were charged for credits you did not authorize or purchase</li>
            </ul>
          </section>

          {/* Exceptional Circumstances */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Exceptional Circumstances</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In addition to your rights under Australian Consumer Law, we may consider refunds 
              in the following exceptional circumstances:
            </p>

            <h3 className="text-lg font-medium text-midnight mb-3">Technical Issues</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Prolonged service outages preventing use of purchased credits</li>
              <li>Technical errors resulting in credits being consumed without service delivery</li>
              <li>System malfunctions causing double-charging or billing errors</li>
            </ul>

            <h3 className="text-lg font-medium text-midnight mb-3">Billing Errors</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Unauthorized charges to your payment method</li>
              <li>Incorrect credit amounts delivered</li>
              <li>Duplicate transactions</li>
            </ul>

            <h3 className="text-lg font-medium text-midnight mb-3">Service Changes</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Significant changes to service functionality affecting credit value</li>
              <li>Discontinuation of service features for which credits were purchased</li>
            </ul>
          </section>

          {/* Refund Process */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">How to Request a Refund</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you believe you are entitled to a refund under Australian Consumer Law or the 
              exceptional circumstances outlined above, please contact us as soon as possible.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-midnight mb-3">Contact Information</h4>
              <p className="text-gray-700 mb-2"><strong>Email:</strong> nick.hull.42@gmail.com</p>
              <p className="text-gray-700 mb-2"><strong>Subject Line:</strong> Refund Request - AI Marker Mate</p>
              <p className="text-gray-700"><strong>Response Time:</strong> We aim to respond within 3 business days</p>
            </div>

            <h3 className="text-lg font-medium text-midnight mb-3">Required Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When requesting a refund, please provide:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Your account email address</li>
              <li>Transaction date and amount</li>
              <li>Payment method used</li>
              <li>Detailed description of the issue</li>
              <li>Any supporting evidence (screenshots, error messages, etc.)</li>
              <li>What remedy you are seeking</li>
            </ul>

            <h3 className="text-lg font-medium text-midnight mb-3">Assessment Process</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We will assess your refund request against:
            </p>
            <ol className="text-gray-700 leading-relaxed mb-4 space-y-2 list-decimal list-inside ml-4">
              <li>Australian Consumer Law requirements</li>
              <li>The exceptional circumstances outlined in this policy</li>
              <li>The specific facts of your case</li>
              <li>Available remedies that may address your concern</li>
            </ol>
          </section>

          {/* Alternative Remedies */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Alternative Remedies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Before processing a refund, we may offer alternative remedies such as:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li><strong>Credit Replacement:</strong> Additional credits to compensate for service issues</li>
              <li><strong>Service Extension:</strong> Extended access to services</li>
              <li><strong>Account Credits:</strong> Credits applied to your account for future use</li>
              <li><strong>Technical Support:</strong> Assistance to resolve service issues</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You are not obligated to accept alternative remedies if you are entitled to a refund 
              under Australian Consumer Law.
            </p>
          </section>

          {/* Processing Times */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Refund Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If a refund is approved:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li><strong>Processing Time:</strong> 5-10 business days from approval</li>
              <li><strong>Refund Method:</strong> Original payment method where possible</li>
              <li><strong>Currency:</strong> Refunded in Australian dollars (AUD)</li>
              <li><strong>Fees:</strong> We do not charge fees for legitimate refund requests</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Note that your bank or payment provider may take additional time to process the refund 
              and make it available in your account.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Dispute Resolution</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are not satisfied with our response to your refund request, you have several options:
            </p>

            <h3 className="text-lg font-medium text-midnight mb-3">Internal Review</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Request a review of our decision by emailing nick.hull.42@gmail.com with 
              "Refund Review Request" in the subject line.
            </p>

            <h3 className="text-lg font-medium text-midnight mb-3">External Dispute Resolution</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li><strong>Australian Competition and Consumer Commission (ACCC):</strong> www.accc.gov.au</li>
              <li><strong>Your state or territory fair trading office</strong></li>
              <li><strong>Australian Financial Complaints Authority (AFCA):</strong> For payment-related disputes - www.afca.org.au</li>
            </ul>

            <h3 className="text-lg font-medium text-midnight mb-3">Legal Action</h3>
            <p className="text-gray-700 leading-relaxed">
              You may also have the right to take legal action. For disputes under $10,000, 
              you may be able to make a claim through your local small claims tribunal.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about this Refund Policy or to request a refund, contact us at:
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-blue-800"><strong>Email:</strong> nick.hull.42@gmail.com</p>
              <p className="text-blue-800"><strong>Business Hours:</strong> Monday to Friday, 9:00 AM - 5:00 PM AEDT</p>
              <p className="text-blue-800 text-sm mt-2">
                We are committed to resolving any issues promptly and fairly while respecting your consumer rights.
              </p>
            </div>
          </section>

          {/* Policy Updates */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Refund Policy from time to time. When we make changes, we will:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside ml-4">
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>Notify users via email or in-app notification</li>
              <li>Highlight significant changes</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Changes will not affect refund requests made before the policy update.
            </p>
          </section>

          {/* Footer Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-8">
            <p className="text-green-800 text-sm">
              <strong>This policy was last updated on {lastUpdated}.</strong> Your rights under Australian 
              Consumer Law cannot be excluded and this policy does not limit those rights. For questions 
              about this policy, please contact nick.hull.42@gmail.com.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
